import { Box, Container, SvgIconProps, Table, TableBody, TableCell, TableHead, TableRow, Typography, TypographyProps, useMediaQuery, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import ImageWrapper from '../ImageWrapper';
import { getImageStyles, tableFromJsonCellStyles, tableFromJsonCtrStyles, tableFromJsonRowStyles, tableFromJsonStyles } from './Table.styles';
import TAGButton from '../TAGButton/TAGButton';
import TAGLink from '../TAGLink';
import TAGSvgIcon, { IconName } from '@shared-components/components/TAGSvgIcon';
import { ContentfulButtonVariant, Maybe, TTabRowVariant } from '@shared-components/types';
import { resolveContentfulButtonVariant } from '@utils';
export interface ITableCellContentStyleProps {
  bgColor: string;
  textVariant: string;
  textColor?: string;
  eyebrowColor: string;
  alignment: 'center' | 'left' | 'right' | 'inherit' | 'justify' | undefined;
  verticalAlignment: string;
  padding: string;
  border: string;
}
interface ITableCellText {
  value: string;
  eyebrow: string;
}
interface ITableCellImage {
  url: string;
}
interface ITableCellIcon {
  name: IconName;
  color: string;
}
interface ITableCellButton {
  value: string;
  variant: string;
  link: string;
}
interface ITableCellLink {
  url: string;
  openInNewTab: boolean;
  value: string;
}
interface ITableCell {
  image: ITableCellImage;
  text: ITableCellText;
  icon: ITableCellIcon;
  link: ITableCellLink;
  button: ITableCellButton;
  styles: ITableCellContentStyleProps;
}
type TRowType = TTabRowVariant | 'body' | 'header';
interface ITableRow {
  cells: Array<ITableCell>;
  rowType?: Maybe<TRowType>;
}
export interface ITableFromJson {
  rows: Array<ITableRow>;
  firstStickyColumnMinWidth?: string;
  fullWidth?: boolean;
  firstRowIsHeader?: boolean;
}
export interface ITableFromJsonProps {
  jsonData?: ITableFromJson;
  firstColumnSticky?: boolean | null;
}
const TableCellContent = (props: {
  cells: Array<ITableCell>;
  isSmallScreen: boolean;
  rowIndex: number;
  isHeader?: boolean;
}) => {
  const {
    cells,
    isSmallScreen,
    rowIndex,
    isHeader = false
  } = props;
  return <>
			{cells.map((cell: ITableCell, index: number) => <Box align={cell.styles.alignment ?? 'center'} key={`table_cell_${rowIndex}_${index}`} data-testid={`table_from_json_table_cell_${rowIndex}_${index}`} sx={tableFromJsonCellStyles(cell.styles)} padding="none" component={TableCell} {...isHeader && {
      component: 'th',
      scope: 'col'
    }}>
					{cell.icon?.name && <TAGSvgIcon icon={cell.icon?.name} htmlColor={cell.icon?.color as SvgIconProps['htmlColor']} data-testid={`table_from_json_table_cell_icon_${rowIndex}_${index}`} />}
					{cell.text?.eyebrow && <Typography variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'} color={cell.styles?.eyebrowColor || 'success.dark'} data-testid={`table_from_json_table_cell_text_eyebrow_${rowIndex}_${index}`}>
							{cell.text.eyebrow}
						</Typography>}
					{cell.text?.value && <Typography variant={cell.styles.textVariant as TypographyProps['variant']} color={cell.styles?.textColor} data-testid={`table_from_json_table_cell_text_value_${rowIndex}_${index}`}>
							<ReactMarkdown>{cell.text.value}</ReactMarkdown>
						</Typography>}
					{cell.image?.url && <ImageWrapper src={cell.image.url} sx={getImageStyles} data-testid={`table_from_json_table_cell_image_${rowIndex}_${index}`} />}
					{cell.link?.url && <TAGLink href={cell.link.url} openInNewTab={cell.link.openInNewTab} data-testid={`table_from_json_table_cell_link_${rowIndex}_${index}`}>
							<ReactMarkdown>{cell.link.value}</ReactMarkdown>
						</TAGLink>}
					{cell.button?.link && <TAGButton size={isSmallScreen ? 'S' : 'M'} variant={resolveContentfulButtonVariant(cell.button.variant as ContentfulButtonVariant, 'secondaryDefault')} dataTestId={`table_from_json_table_cell_button_${rowIndex}_${index}`} href={cell.button?.link}>
							<ReactMarkdown>{cell.button.value}</ReactMarkdown>
						</TAGButton>}
				</Box>)}
		</>;
};
const TableRowContent = (props: {
  row: ITableRow;
  isSmallScreen: boolean;
  index: number;
  firstColumnSticky: boolean | null;
  firstStickyColumnMinWidth: string;
  isHeader?: boolean;
}) => {
  const {
    row,
    isSmallScreen,
    index,
    firstColumnSticky = true,
    firstStickyColumnMinWidth,
    isHeader = false
  } = props;
  return <Box key={`table_from_json_${row.cells?.length}_${index}`} data-testid={`table_from_json_table_row_${index}`} component={TableRow} sx={tableFromJsonRowStyles(firstColumnSticky && isSmallScreen, firstStickyColumnMinWidth)} data-sentry-element="Box" data-sentry-component="TableRowContent" data-sentry-source-file="TableFromJson.tsx">
			<TableCellContent cells={row?.cells} isSmallScreen={isSmallScreen} rowIndex={index} isHeader={isHeader} data-sentry-element="TableCellContent" data-sentry-source-file="TableFromJson.tsx" />
		</Box>;
};
export default function TableFromJson({
  jsonData,
  firstColumnSticky = true
}: Readonly<ITableFromJsonProps>) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (!jsonData) return null;
  const {
    rows,
    firstStickyColumnMinWidth = '',
    fullWidth = false,
    firstRowIsHeader = false
  } = jsonData;
  return <Container component={Table} disableGutters maxWidth={false} sx={tableFromJsonCtrStyles} data-testid="table_from_json_table" data-sentry-element="Container" data-sentry-component="TableFromJson" data-sentry-source-file="TableFromJson.tsx">
			{firstRowIsHeader && <Box sx={tableFromJsonStyles(fullWidth)} data-testid="table_from_json_table_header" component={TableHead}>
					<TableRowContent row={rows[0]} isSmallScreen={isSmallScreen} index={0} key={`table_from_json_${rows[0].cells.length}_${0}`} firstColumnSticky={firstColumnSticky} firstStickyColumnMinWidth={firstStickyColumnMinWidth} isHeader={true} />
				</Box>}
			<Box sx={tableFromJsonStyles(fullWidth)} data-testid="table_from_json_table_body" component={TableBody} data-sentry-element="Box" data-sentry-source-file="TableFromJson.tsx">
				{rows.map((row: ITableRow, index: number) =>
      // Skip the first row if it's a header
      firstRowIsHeader && index === 0 ? null : <TableRowContent row={row} isSmallScreen={isSmallScreen} index={index} key={`table_from_json_${row.cells.length}_${index}`} firstColumnSticky={firstColumnSticky} firstStickyColumnMinWidth={firstStickyColumnMinWidth} />)}
			</Box>
		</Container>;
}